import { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api } from "../lib/utils";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import Pagination from "../components/ui/pagination";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Plus, Edit, Trash2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useEffect } from "react";
import moment from "moment-timezone";
const Swal = require("sweetalert2");

const ManageCategories = () => {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [departmentFilter, setDepartmentFilter] = useState("All Departments");
  const [searchItem, setSearchItem] = useState("");
  const [pagination, setPagination] = useState({ currentPage: 1 });
  const [pageNo, setPageNo] = useState(1);
  const [errors, setErrors] = useState({});
  const [loader, setLoader] = useState(false);
  const [adminAndFinAccessUsers, setAdminAndFinAccessUsers] = useState([]);
  const departments = ["HR", "INFRA", "FINANCE"];
  const slaUnits = ["hours", "days"];

  let userData = null;
  try {
    userData = typeof user === "string" ? JSON.parse(user) : user;
  } catch (e) {
    userData = user;
  }
  const isManager =
    userData?.Department === "HR" || userData?.Department === "HRD"
      ? "HR"
      : userData?.Department === "Finance"
      ? "FINANCE"
      : userData?.Department === "Administration"
      ? "ADMIN"
      : "";
  const [formData, setFormData] = useState({
    department: isManager ? isManager : "",
    category_name: "",
    sub_category_name: "",
    sla: "",
    slaUnit: "days",
  });

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
    getCategories(newPage);
  };

  useEffect(() => {
    getAdminAndFinUsers();
  }, []);

  useEffect(() => {
    if (searchItem) {
      const delayDebounce = setTimeout(() => {
        getCategories(pageNo);
      }, 350);

      return () => clearTimeout(delayDebounce);
    } else {
      getCategories(pageNo);
    }
  }, [searchItem, departmentFilter, pageNo]);

  useEffect(() => {
    if (isManager) {
      setDepartmentFilter(isManager);
    }
  }, [isManager, formData.department]);

  const getAdminAndFinUsers = async () => {
    try {
      const result = await api.get(`/api/categories/getAdminAnFinUsers`);

      if (result) {
        const emailIds = result?.data;
        const formattedEmailIds = emailIds.map((elem) => elem?.email);
        const bothAdminAndFinRoles = formattedEmailIds;
        setAdminAndFinAccessUsers(bothAdminAndFinRoles);
      } else {
        setAdminAndFinAccessUsers([]);
      }
    } catch (err) {
      return err;
    }
  };

  const getCategories = async (page) => {
    try {
      let apiCall = `/api/categories/getAllCategories?page=${page}&limit=10`;
      let callApi = false;
      if (
        formData.department !== "All Departments" &&
        !searchItem &&
        departmentFilter !== "All Departments"
      ) {
        apiCall += `&department=${departmentFilter}`;
        callApi = true;
      } else if (
        formData.department !== "All Departments" &&
        searchItem &&
        departmentFilter !== "All Departments"
      ) {
        apiCall += `&department=${departmentFilter}&searchItem=${searchItem}`;
        callApi = true;
      } else if (!formData.department && searchItem && !departmentFilter) {
        apiCall += `&searchItem=${searchItem}`;
        callApi = true;
      } else if (departmentFilter !== "All Departments") {
        apiCall += `&department=${departmentFilter}`;
        callApi = true;
      } else if (userData.Department === "Management") {
        apiCall = apiCall;
        callApi = true;
      }
      setLoader(true);
      if (callApi) {
        const result = await api.get(apiCall);
        setCategories(result?.data);
        setPagination(result?.pagination);
        setLoader(false);
      }
    } catch (err) {
      return err;
    }
  };

  const getCategoryIdWise = async (cat) => {
    try {
      const result = await api.get(`/api/categories/getCategory/${cat?.id}`);
      if (result?.data && result?.data?.length !== 0) {
        const [slaValue, slaUnit] = result?.data[0]?.sla.split(" ");
        setFormData({
          department:
            result?.data[0].department === "ADMIN"
              ? "INFRA"
              : result?.data[0].department,
          category_name: result?.data[0].category_name,
          sub_category_name: result?.data[0].sub_category_name,
          sla: slaValue,
          slaUnit: slaUnit || "days",
        });
      }
    } catch (err) {
      return err;
    }
  };

  const updateCategory = async () => {
    const updateData = {
      ...formData,
      department:
        formData.department === "INFRA" ? "ADMIN" : formData.department,
      sla: formData?.sla ? `${formData?.sla} ${formData?.slaUnit}` : "",
    };
    const result = await api.put(
      `/api/categories/updateCategory/${editingId}`,
      updateData,
    );
    if (result) {
      toast.success(result?.message);
    }
  };

  const createCategory = async (category) => {
    try {
      // Fetch all tickets without pagination to enable client-side filtering
      const response = await api.post(
        `/api/categories/createCategories`,
        category,
      );
      setLoader(false);
      toast.success(response.message);
      await getCategories(1);
    } catch (error) {
      console.error("Failed to fetch all tickets:", error);
    }
  };

  const deleteCategory = async (categoryId) => {
    try {
      const result = await api.post(
        `/api/categories/deleteCategory/${categoryId}`,
      );
      if (result) {
        toast.success(result?.message);
        getCategories(1);
      }
    } catch (err) {
      return `Something went wrong, ${err}`;
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.department) newErrors.department = "Department is required";
    if (!formData.category_name.trim())
      newErrors.category_name = "Category name is required";
    if (!formData.sub_category_name.trim())
      newErrors.sub_category_name = "Sub-category name is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClearFilter = () => {
    if (isManager) {
      setSearchItem("");
      setDepartmentFilter(isManager);
    } else {
      setSearchItem("");
      setDepartmentFilter("All Departments");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const slaText = `${formData.sla} ${formData.slaUnit}`;

    if (editingId) {
      // Update existing category
      setCategories((prev) =>
        prev.map((cat) =>
          cat.id === editingId
            ? {
                ...cat,
                department:
                  formData.department === "ADMIN"
                    ? "INFRA"
                    : formData.department,
                category_name: formData.category_name,
                sub_category_name: formData.sub_category_name,
                sla: formData.sla ? slaText : "",
              }
            : cat,
        ),
      );

      updateCategory();
      setEditingId(null);
    } else {
      // Add new category
      const newCategory = {
        department:
          formData.department === "INFRA" ? "ADMIN" : formData.department,
        category_name: formData.category_name,
        sub_category_name: formData.sub_category_name,
        sla: formData.sla ? slaText : "",
      };
      setCategories((prev) => [...prev, newCategory]);
      createCategory(newCategory);
    }

    // Reset form
    setFormData({
      department: "",
      category_name: "",
      sub_category_name: "",
      sla: "",
      slaUnit: "days",
    });
    setIsDialogOpen(false);
    setErrors({});
  };

  const handleEdit = (category) => {
    setEditingId(category.id);
    getCategoryIdWise(category);
    setIsDialogOpen(true);
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        await deleteCategory(id);
        Swal.fire({
          title: "Deleted!",
          text: "Your file has been deleted.",
          icon: "success",
        });
      }
    });
  };

  const handleAddNew = () => {
    setFormData({
      department: isManager ? isManager : "",
      category_name: "",
      sub_category_name: "",
      sla: "",
      slaUnit: "days",
    });
    setEditingId(null);
    setErrors({});
    setIsDialogOpen(true);
  };

  const getDepartmentColor = (department) => {
    const colors = {
      HR: "bg-green-100 text-green-800",
      ADMIN: "bg-blue-100 text-blue-800",
      FINANCE: "bg-purple-100 text-purple-800",
    };
    return (
      colors[department === "INFRA" ? "ADMIN" : department] ||
      "bg-gray-100 text-gray-800"
    );
  };

  return (
    <div className="p-6 space-y-6 font-poppins">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className=" font-bold" style={{ fontSize: "1.285rem" }}>
            Manage Categories & SLA
          </h1>
          <p className="text-gray-600 font-body mt-1">
            Configure ticket categories and their service level agreements
          </p>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={handleAddNew} className="gap-2">
              <Plus className="h-4 w-4" />
              Add New Category
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingId ? "Edit Category" : "Add New Category"}
              </DialogTitle>
              <DialogDescription className="font-body">
                {editingId
                  ? "Update the category details below."
                  : "Create a new ticket category and set its SLA."}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="department" className="font-body">
                  Department
                </Label>
                <Select
                  value={formData.department}
                  onValueChange={(value) => {
                    setFormData((prev) => ({ ...prev, department: value }));
                    if (errors.department)
                      setErrors((prev) => ({ ...prev, department: "" }));
                  }}
                >
                  <SelectTrigger
                    className={errors.department ? "border-red-500" : ""}
                  >
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    {departments.map((dept) => (
                      <SelectItem
                        key={dept}
                        value={dept}
                        disabled={
                          // Admin and Finance users can select both INFRA and FINANCE
                          userData?.Department === "Administration" || userData?.Department === "Finance"
                            ? !["INFRA", "FINANCE"].includes(dept)
                            : // HR users can only select HR
                              isManager && (dept === "INFRA" ? "ADMIN" : dept) !== isManager
                        }
                      >
                        {dept}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.department && (
                  <p className="text-sm text-red-500 font-body">
                    {errors.department}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="categoryName" className="font-body">
                  Category Name
                </Label>
                <Input
                  id="categoryName"
                  value={formData.category_name}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      category_name: e.target.value,
                    }));
                    if (errors.category_name)
                      setErrors((prev) => ({ ...prev, category_name: "" }));
                  }}
                  placeholder="e.g., Leave Management"
                  className={errors.category_name ? "border-red-500" : ""}
                />
                {errors.category_name && (
                  <p className="text-sm text-red-500 font-body">
                    {errors.category_name}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="subcategoryName" className="font-body">
                  Sub-category Name
                </Label>
                <Input
                  id="subcategoryName"
                  value={formData.sub_category_name}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      sub_category_name: e.target.value,
                    }));
                    if (errors.sub_category_name)
                      setErrors((prev) => ({ ...prev, sub_category_name: "" }));
                  }}
                  placeholder="e.g., Annual Leave"
                  className={errors.sub_category_name ? "border-red-500" : ""}
                />
                {errors.sub_category_name && (
                  <p className="text-sm text-red-500 font-body">
                    {errors.sub_category_name}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="font-body">SLA</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    value={formData.sla}
                    onChange={(e) => {
                      setFormData((prev) => ({ ...prev, sla: e.target.value }));
                      if (errors.sla)
                        setErrors((prev) => ({ ...prev, sla: "" }));
                    }}
                    placeholder="e.g., 2"
                    className={`flex-1 ${errors.sla ? "border-red-500" : ""}`}
                    min="1"
                  />
                  <Select
                    value={formData.slaUnit}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, slaUnit: value }))
                    }
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {slaUnits.map((unit) => (
                        <SelectItem key={unit} value={unit}>
                          {unit}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {errors.sla && (
                  <p className="text-sm text-red-500 font-body">{errors.sla}</p>
                )}
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  className="font-body"
                >
                  Cancel
                </Button>
                <Button type="submit" className="font-body">
                  {editingId ? "Update" : "Create"} Category
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-4">
        <div className="flex flex-col gap-2">
          <Label
            htmlFor="priority-filter"
            className="text-sm font-medium font-poppins"
          >
            Department
          </Label>
          <Select
            value={departmentFilter}
            onValueChange={setDepartmentFilter}
            disabled={
              // Admin and Finance users can filter by any department
              userData?.Department === "Administration" || userData?.Department === "Finance"
                ? false
                : // HR users can only filter by their department
                  isManager && departmentFilter.toUpperCase() !== isManager.toUpperCase()
            }
          >
            <SelectTrigger
              className="w-[180px] font-poppins"
              id="priority-filter"
            >
              <SelectValue placeholder="All Departments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All Departments" disabled={isManager}>
                All Departments
              </SelectItem>
              <SelectItem
                value="ADMIN"
                disabled={
                  userData?.Department === "Administration" || userData?.Department === "Finance"
                    ? false
                    : isManager && isManager.toUpperCase() !== "ADMIN"
                }
              >
                Infra
              </SelectItem>
              <SelectItem
                value="HR"
                disabled={isManager && isManager.toUpperCase() !== "HR"}
              >
                HR
              </SelectItem>
              <SelectItem
                value="FINANCE"
                disabled={
                  userData?.Department === "Administration" || userData?.Department === "Finance"
                    ? false
                    : isManager && isManager.toUpperCase() !== "FINANCE"
                }
              >
                Finance
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="max-w-xl w-[400px]">
          <Input
            type="text"
            placeholder="Search categories by department"
            value={searchItem}
            onChange={(e) => setSearchItem(e.target.value)}
            className="font-poppins mt-6"
          />
        </div>
        {(!isManager && departmentFilter !== "All Departments") ||
        (isManager && searchItem) ||
        searchItem ? (
          <button
            className="text-blue-600 hover:text-blue-800 text-sm underline cursor-pointer font-poppins  mt-5"
            onClick={() => handleClearFilter()}
          >
            Clear Filter
          </button>
        ) : null}
      </div>

      {/* Categories Table */}
      <Card>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-heading">Department</TableHead>
                <TableHead className="font-heading">Category Name</TableHead>
                <TableHead className="font-heading">
                  Sub-category Name
                </TableHead>
                <TableHead className="font-heading">SLA</TableHead>
                <TableHead className="font-heading">Created By</TableHead>
                <TableHead className="font-heading">Created Date</TableHead>
                <TableHead className="font-heading text-right">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loader ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="text-gray-500 font-body">LOADING...</div>
                  </TableCell>
                </TableRow>
              ) : categories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="text-gray-500 font-body">
                      No categories found. Add your first category to get
                      started.
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                categories.map((category) => (
                  <TableRow key={category.id}>
                    <TableCell>
                      <Badge
                        variant="secondary"
                        className={`${getDepartmentColor(
                          category?.department,
                        )} font-body`}
                      >
                        {category?.department === "ADMIN"
                          ? "INFRA"
                          : category?.department}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium font-body">
                      {category?.category_name}
                    </TableCell>
                    <TableCell className="font-body">
                      {category?.sub_category_name}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-body">
                        {category?.sla ? category?.sla : "NA"}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-body">
                      {category?.created_by}
                    </TableCell>
                    <TableCell className="font-body">
                      {moment(category?.createdAt).format(
                        "YYYY-MM-DD hh:mm:ss A",
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(category)}
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Trash2
                          className="h-4 w-4 cursor-pointer"
                          onClick={() => handleDelete(category?.id)}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          {pagination?.totalPages > 1 && (
            <div className="mt-6">
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                totalCount={pagination.totalCount}
                limit={pagination.limit}
                hasNextPage={pagination.hasNextPage}
                hasPrevPage={pagination.hasPrevPage}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ManageCategories;
